import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Copy, Check, X, CircleDot } from "lucide-react";
import { toast } from "sonner";
import { TONES } from "@/lib/relationsData";

const INK = "#33251A";
const SOFT = "rgba(51,37,26,0.62)";

export default function SituationPath({ s, done, onDone, onBack }) {
  const [tone, setTone] = useState("warm");

  const copy = () => {
    navigator.clipboard?.writeText(s.script[tone]);
    toast("Copied. Change any word you like before you send it.");
  };

  return (
    <motion.div initial={{ opacity: 0, y: 26 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }} className="w-full" data-testid={`situation-path-${s.key}`}>
      <button onClick={onBack} className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-[12px] font-semibold transition-transform hover:-translate-x-0.5"
        style={{ background: "rgba(255,255,255,0.85)", border: "1px solid rgba(51,37,26,0.09)", color: INK }} data-testid="situation-back">
        <ArrowLeft size={13} /> Another sentence
      </button>

      <div className="mt-7 grid grid-cols-1 lg:grid-cols-12 gap-9 items-start">
        <div className="lg:col-span-7">
          <p className="text-[10px] tracking-[0.24em] uppercase font-semibold" style={{ color: s.tone }}>{s.who}</p>
          <h1 className="font-display text-[32px] sm:text-[46px] leading-[1.02] mt-3" style={{ color: INK }}>“{s.say}”</h1>
          <p className="text-[16px] mt-6 leading-[1.85] max-w-2xl" style={{ color: SOFT }}>{s.read}</p>

          <div className="mt-8 rounded-[1.6rem] p-6 sm:p-7 relative overflow-hidden" style={{ background: `${s.tone}16`, border: `1px solid ${s.tone}3A` }} data-testid="situation-move">
            <p className="text-[10px] tracking-[0.2em] uppercase font-semibold" style={{ color: s.tone }}>The first move</p>
            <p className="font-display text-[24px] sm:text-[28px] mt-2 leading-tight" style={{ color: INK }}>{s.move.t}</p>
            <p className="text-[15px] mt-3 leading-relaxed" style={{ color: SOFT }}>{s.move.body}</p>
          </div>

          <div className="mt-4 rounded-[1.6rem] p-6 sm:p-7" style={{ background: "rgba(255,255,255,0.8)", border: "1px solid rgba(51,37,26,0.08)" }}>
            <p className="text-[10px] tracking-[0.2em] uppercase font-semibold" style={{ color: "rgba(51,37,26,0.4)" }}>What makes it worse</p>
            <div className="mt-4 space-y-2.5">
              {s.avoid.map((a, i) => (
                <motion.div key={a} initial={{ opacity: 0, x: -12 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.07 }} className="flex items-start gap-3">
                  <X size={13} className="mt-1 shrink-0" style={{ color: "#C4553F" }} />
                  <p className="text-[14.5px]" style={{ color: INK }}>{a}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>

        {/* the script */}
        <div className="lg:col-span-5 lg:sticky lg:top-24">
          <div className="rounded-[1.8rem] overflow-hidden" style={{ background: "#fff", border: "1px solid rgba(51,37,26,0.09)", boxShadow: "0 44px 84px -60px rgba(51,37,26,0.9)" }}>
            <div className="px-6 pt-6">
              <p className="text-[10px] tracking-[0.2em] uppercase font-semibold" style={{ color: s.tone }}>Something you could actually send</p>
              <div className="flex gap-1.5 mt-4 p-1 rounded-full" style={{ background: "rgba(51,37,26,0.05)" }}>
                {TONES.map((t) => (
                  <button key={t.k} onClick={() => setTone(t.k)} className="flex-1 rounded-full py-2 text-[12.5px] font-semibold transition-colors"
                    style={tone === t.k ? { background: "#fff", color: INK, boxShadow: "0 8px 18px -10px rgba(51,37,26,0.5)" } : { color: "rgba(51,37,26,0.45)" }}
                    data-testid={`situation-tone-${t.k}`}>{t.label}</button>
                ))}
              </div>
            </div>
            <div className="px-6 py-6">
              <AnimatePresence mode="wait">
                <motion.p key={tone} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.3 }}
                  className="text-[16px] leading-[1.75]" style={{ color: INK }} data-testid="situation-script">{s.script[tone]}</motion.p>
              </AnimatePresence>
              <button onClick={copy} className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full px-5 py-3.5 text-sm font-semibold text-white"
                style={{ background: s.tone }} data-testid="situation-copy">
                <Copy size={13} /> Copy this version
              </button>
            </div>
          </div>

          <div className="mt-4 rounded-[1.6rem] p-6" style={{ background: "linear-gradient(150deg, #33251A, #55391F)" }} data-testid="situation-action">
            <p className="text-[10px] tracking-[0.2em] uppercase font-semibold" style={{ color: "#E89A4F" }}>One outward action</p>
            <p className="font-display text-[21px] mt-2 leading-snug text-white">{s.action}</p>
            <button onClick={() => onDone(!done)} className="mt-5 inline-flex items-center gap-2.5 rounded-full px-5 py-3 text-[13px] font-semibold transition-transform hover:-translate-y-0.5"
              style={done ? { background: "#E89A4F", color: "#33251A" } : { background: "rgba(255,255,255,0.1)", color: "#fff", border: "1px solid rgba(255,255,255,0.2)" }}
              data-testid="situation-action-done">
              {done ? <><Check size={13} /> Done, and logged privately</> : <><CircleDot size={13} /> I did this</>}
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
